/**
 * useMentionThreadClosure — the impure half of "is this thread closed to me"
 * (2026-09-21 owner directive).
 *
 * The HOST owns every piece of data access and hands it in, the
 * `useThreadActivity` seam shape — so the whole of the resolution ladder is
 * exercisable with three plain functions and no emulator.
 *
 * THE LADDER, cheapest first (threadClosure.resolveClosureState is the pure
 * rule; this hook is only the plumbing that feeds it):
 *
 *   1. present in the LIVE index      → open      · zero reads, always right
 *   2. present in the tombstone map   → retired   · zero reads, survives reload
 *   3. present in the ARCHIVED index  → retired   · one listener, with attribution
 *   4. otherwise                      → unknown   · one bounded, cached doc read
 *
 * WHY NOT `subscribeThreadArchiveNotice`. It looks like the obvious channel and
 * it cannot work, for two measured reasons. Its `emit()` reads the module's
 * `current` INSIDE the forEach callback, and GlobalChatSystem's existing
 * subscriber clears synchronously — so the second subscriber is handed `null`
 * and then nothing. And even fixed it would be nearly blind: the passing notice
 * requires the thread to have been in the PREVIOUS snapshot and to carry
 * `sharedThread === true`, and bails when `archivedBy` is blank or self — so it
 * fires for ONE of the ways a thread retires, and for the janitor it fires
 * falsely. The archived listener is the live channel instead.
 *
 * EVERY FAILURE FALLS TOWARD "OPEN". The archive fanout skips a member whose
 * index doc does not exist yet (`if (!current.exists) return;`), so a
 * fail-closed reading would grey a freshly-invited member's row while their
 * thread is perfectly live. A thrown read leaves the id unanswered so a later
 * pass retries; an absent doc reads as open. The refusal lives at the door.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ThreadClosureFacts } from '../../services/groupThreads/threadClosure';
import { MAX_CLOSURE_PROBE_ATTEMPTS, planClosureProbes } from '../../services/groupThreads/threadClosure';

/**
 * The retry bound moved to threadClosure on 2026-09-21, when the bell's store
 * needed the same number: one owner, so the two consumers cannot drift. Kept
 * exported here under the same name for every existing import site.
 */
export { MAX_CLOSURE_PROBE_ATTEMPTS };

/** Stable identity for the lever-off path, so no consumer re-memoizes. */
const EMPTY_CLOSURE_MAP: ReadonlyMap<string, ThreadClosureFacts> = Object.freeze(
  new Map<string, ThreadClosureFacts>(),
) as ReadonlyMap<string, ThreadClosureFacts>;

export interface MentionThreadClosureApi {
  /** Thread ids the surfaces are asking about, in the order shown. */
  threadIds: readonly string[];
  /** Thread ids the viewer's live (unarchived) index holds. Presence ⇒ open. */
  liveThreadIds: ReadonlySet<string>;
  /** Durable-tombstone probe. Zero reads, cross-tab, survives reload. */
  isLocallyArchived: (threadId: string) => boolean;
  /** One single-doc read of the viewer's own index doc. */
  readArchiveInfo: (threadId: string) => Promise<ThreadClosureFacts>;
  /** Attach the archived-index listener. Returns its detach. */
  subscribeArchived: (cb: (facts: ReadonlyArray<ThreadClosureFacts & { threadId: string }>) => void) => () => void;
  /** Off ⇒ no listener, no probe, a stable frozen empty map. */
  enabled: boolean;
}

export interface MentionThreadClosureResult {
  /** Facts for every thread anything has answered for. */
  closedThreads: ReadonlyMap<string, ThreadClosureFacts>;
  /**
   * The door's latch. Pressing a row spends one read where latency is already
   * expected and the answer is guaranteed fresh; latching it means a row that
   * rendered open because nothing knew better greys permanently afterwards,
   * instead of dead-ending twice.
   */
  noteClosure: (threadId: string, facts: ThreadClosureFacts) => void;
}

export function useMentionThreadClosure(api: MentionThreadClosureApi): MentionThreadClosureResult {
  const [facts, setFacts] = useState<ReadonlyMap<string, ThreadClosureFacts>>(EMPTY_CLOSURE_MAP);
  const factsRef = useRef<Map<string, ThreadClosureFacts>>(new Map());
  /** Ids a probe answered NEGATIVELY — stop asking. */
  const answeredRef = useRef<Set<string>>(new Set());
  const inFlightRef = useRef<Set<string>>(new Set());
  /**
   * Failed attempts per id, and the tick that re-runs the effect after one.
   *
   * A THROWN read is not an answer — it must be re-asked, or a single offline
   * moment silences the ladder for the life of the hook. But the effect keys on
   * the PLAN's value, and a failure changes neither the plan nor the facts, so
   * without this counter the retry the header promises would never fire. (It
   * did not: the pin caught the promise being empty.) Bounded, because a
   * permanently failing read must degrade to "renders open" rather than spin.
   */
  const failuresRef = useRef<Map<string, number>>(new Map());
  const [retryTick, setRetryTick] = useState(0);

  // The live inputs ride a ref so the probe effect depends on the ids it must
  // resolve, never on the identity of the host's callbacks — a keystroke
  // elsewhere in the chat must not re-issue every read.
  const apiRef = useRef(api);
  apiRef.current = api;

  /**
   * Latch one thread's facts. ONE-WAY, exactly like archiving itself: a later
   * negative never clears a positive, because a positive can only have come
   * from a tombstone or a document that said so, while a negative can be a read
   * that raced an archive write still in flight.
   */
  const latch = useCallback((threadId: string, next: ThreadClosureFacts): void => {
    const id = String(threadId || '').trim();
    if (!id) return;
    const current = factsRef.current.get(id);
    if (current?.archived === true && next.archived !== true) return;
    // Nothing changed ⇒ no state write, so a repeated snapshot does not repaint.
    if (current
      && current.archived === next.archived
      && current.archivedBy === next.archivedBy
      && current.archivedByName === next.archivedByName
      && current.atMs === next.atMs) return;
    factsRef.current.set(id, next);
    setFacts(new Map(factsRef.current));
  }, []);

  const noteClosure = useCallback((threadId: string, next: ThreadClosureFacts): void => {
    if (!apiRef.current.enabled) return;
    if (next.archived !== true) {
      const id = String(threadId || '').trim();
      if (id) answeredRef.current.add(id);
      return;
    }
    latch(threadId, next);
  }, [latch]);

  // ── tier 2: the tombstone map, for whatever is being asked about ─────────
  // Zero reads. Runs on the id list rather than eagerly over storage, so a
  // thread nobody is looking at costs nothing. No attribution is latched here,
  // deliberately: addArchivedTombstone is also called by the passing-notice
  // probe and the stale-draft tidy, so a tombstone does NOT prove self-archive
  // and "you archived this" would be a lie.
  const idsKey = api.enabled ? api.threadIds.join(',') : '';
  useEffect(() => {
    if (!apiRef.current.enabled) return;
    for (const raw of apiRef.current.threadIds) {
      const id = String(raw || '').trim();
      if (!id || factsRef.current.get(id)?.archived === true) continue;
      if (apiRef.current.liveThreadIds.has(id)) continue;
      if (apiRef.current.isLocallyArchived(id)) latch(id, { archived: true, atMs: 0 });
    }
  }, [idsKey, latch]);

  // ── tier 3: the archived-index listener ─────────────────────────────────
  const listenerOn = api.enabled;
  useEffect(() => {
    if (!listenerOn) return undefined;
    return apiRef.current.subscribeArchived((rows) => {
      for (const row of rows) latch(row.threadId, row);
    });
  }, [listenerOn, latch]);

  // ── tier 4: the bounded, cached probe ───────────────────────────────────
  const plan = useMemo(() => {
    if (!api.enabled) return { threadIds: [] as string[], truncated: false };
    return planClosureProbes({
      threadIds: api.threadIds,
      liveThreadIds: api.liveThreadIds,
      answered: answeredRef.current,
      inFlight: inFlightRef.current,
      tombstoned: api.isLocallyArchived,
      // A thread already latched needs nothing.
      cap: undefined,
    });
    // `facts` is in the deps because a latch retires ids from the plan.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api.enabled, idsKey, api.liveThreadIds, facts]);

  // The plan's identity changes every render (fresh array), so the effect keys
  // on its VALUE — the planKey trick from useThreadActivity.
  const planKey = plan.threadIds.join(',');
  useEffect(() => {
    if (!apiRef.current.enabled) return undefined;
    const pending = plan.threadIds.filter((id) => !factsRef.current.has(id));
    if (pending.length === 0) return undefined;
    let cancelled = false;
    for (const id of pending) inFlightRef.current.add(id);
    void (async () => {
      await Promise.all(pending.map(async (id) => {
        try {
          const info = await apiRef.current.readArchiveInfo(id);
          if (cancelled) return;
          if (info.archived === true) latch(id, info);
          // A doc that says "not archived", or no doc at all, is answered and
          // never re-read. Renders OPEN — the safe direction (see the header).
          else answeredRef.current.add(id);
        } catch {
          // Not an answer: render it open (today's behaviour) and ask again,
          // up to the bound. Greying somebody's whole mentions list on a
          // network hiccup is the one failure this must never have; so is
          // hammering a read that will never succeed.
          const attempts = (failuresRef.current.get(id) || 0) + 1;
          failuresRef.current.set(id, attempts);
          if (attempts >= MAX_CLOSURE_PROBE_ATTEMPTS) answeredRef.current.add(id);
          else if (!cancelled) setRetryTick((n) => n + 1);
        } finally {
          inFlightRef.current.delete(id);
        }
      }));
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planKey, retryTick, latch]);

  return {
    closedThreads: api.enabled ? facts : EMPTY_CLOSURE_MAP,
    noteClosure,
  };
}
